import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import {
  AddOnlineMeetingIcon,
  CellPhoneIcon,
  ChevronDownMedIcon,
  ChevronRightMedIcon,
  DownloadIcon,
  NetworkTowerIcon,
  ParkingLocationIcon,
  PhoneIcon,
  TelemarketerIcon
} from "@fluentui/react-icons-mdl2";
import { useCallback, useMemo, useState } from "react";

import {
  Invoice,
  InvoiceStatusEnum,
  InvoiceStatusLabel,
  Tariff,
  PaginationInfo
} from "../../interfaces/invoices-response";
import InvoiceResume from "../invoiceResume/invoice-resume";
import { PaginationContainer } from "../invoiceSidebar/style";
import {
  TariffContainer,
  TariffContent,
  TariffName
} from "../tariffItem/style";
import TariffItem from "../tariffItem/tariff-item";

import {
  Card,
  ChevronAndBadgeContainer,
  Content,
  Header,
  HeaderContent,
  InvoiceBadge,
  InvoiceInfo,
  InvoiceServiceContainer,
  InvoiceTitle,
  InvoiceTotal,
  Separator,
  TariffList
} from "./style";

import { ModalListItens } from "@/components/ModalSelect";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useFileDownloader } from "@/hooks/useFileDownloader";
import { parseDateToMonthYear } from "@/utils/FormatDate";
import { formatPhoneAndSerialNumberMask } from "@/utils/PhoneAndSerialNumberMask/formatPhoneAndSerialNumber";

interface InvoiceCardProps {
  invoice: Invoice;
  expanded: boolean;
  selectedTariff?: Tariff;
  onToggle: (invoiceId: number) => void;
  setSelectedTariff: (tariff: Tariff) => void;
  customTariffs?: Tariff[];
  isLoadingTariffs?: boolean;
  currentInvoiceId?: number;
  document?: string;
  onLoadMoreTariffs?: (invoiceId: number) => void;
  onLoadPreviousTariffs?: (invoiceId: number) => void;
  pagination?: Record<number, PaginationInfo>;
}

export default function InvoiceCard({
  invoice,
  expanded,
  selectedTariff,
  onToggle,
  setSelectedTariff,
  customTariffs,
  isLoadingTariffs = false,
  currentInvoiceId,
  document,
  onLoadMoreTariffs,
  onLoadPreviousTariffs,
  pagination
}: InvoiceCardProps) {
  const { month, year } = parseDateToMonthYear(invoice?.dueDate);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { execute } = useApi();

  const { isDownloading: isExportingTariffs, startDownload } =
    useFileDownloader({
      defaultFileName: `tarifas-compactadas-${invoice.id}.zip`,
      onSuccess: () => setIsModalOpen(false)
    });

  const invoiceStatusMap: Record<InvoiceStatusEnum, InvoiceStatusLabel> = {
    [InvoiceStatusEnum.OPEN]: InvoiceStatusLabel.OPEN,
    [InvoiceStatusEnum.CLOSED]: InvoiceStatusLabel.CLOSED,
    [InvoiceStatusEnum.CANCELED]: InvoiceStatusLabel.CANCELED
  };

  const getServiceIcon = (serviceType: string) => {
    switch (serviceType) {
      case "Telefonia":
        return <CellPhoneIcon />;
      case "Tag do Sicoob":
        return <ParkingLocationIcon />;
      case "Rastreador":
        return <NetworkTowerIcon />;
      case "Conectividade Fixa":
        return <PhoneIcon />;
      case "PABX":
        return <AddOnlineMeetingIcon />;
      case "Linhas de Telemetria":
        return <NetworkTowerIcon />;
      case "Projeto 24h Segurado Sicoob":
        return <TelemarketerIcon />;
      default:
        return <CellPhoneIcon />;
    }
  };

  const renderServices = (services: string[]) => {
    return (
      <>
        {services.map((service, index) => (
          <span key={index}>{getServiceIcon(service)}</span>
        ))}
        {services.length <= 1 && <p>{services.join(", ")}</p>}
      </>
    );
  };

  const tariffsToShow = useMemo(() => {
    return currentInvoiceId === invoice.id &&
      customTariffs &&
      customTariffs.length > 0
      ? customTariffs
      : [];
  }, [currentInvoiceId, invoice.id, customTariffs]);

  const currentPage = pagination?.[invoice.id]?.page || 1;
  const hasMorePages = pagination?.[invoice.id]?.hasMore || false;
  const totalPages = pagination?.[invoice.id]?.totalPages || 0;
  const showPagination =
    expanded && tariffsToShow.length > 0 && currentInvoiceId === invoice.id;

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleModalAction = useCallback(
    async (selectedIds: number[]) => {
      const response = await execute({
        url: `invoices/compact-tariffs`,
        method: "POST",
        params: {
          document
        },
        bodyObj: {
          tariffIds: selectedIds,
          invoiceId: invoice.id
        },
        showAlert: true,
        messages: {
          success: "Tarifas exportadas com sucesso!",
          error: "Erro ao exportar tarifas."
        }
      });

      if (!response.error && response.data instanceof ArrayBuffer) {
        startDownload({
          data: response.data
        });
      }
    },
    [execute, invoice.id, startDownload, document]
  );

  const modalTariffItems = tariffsToShow.map(tariff => ({
    id: tariff.id,
    text: tariff.identifier
      ? `${formatPhoneAndSerialNumberMask(tariff.identifier)} `
      : "Identificador não disponível",
    required: false
  }));

  const preparedTariffs = tariffsToShow.map(tariff => ({
    ...tariff,
    invoiceId: tariff.invoiceId || invoice.id
  }));

  return (
    <Card>
      <Header onClick={() => onToggle(invoice.id)}>
        <HeaderContent>
          <InvoiceInfo>
            <InvoiceTitle>{`${month} - ${year}`}</InvoiceTitle>
            <InvoiceServiceContainer>
              {renderServices(invoice.products)}
            </InvoiceServiceContainer>

            <InvoiceTotal>
              {Number(invoice.value).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL"
              })}
            </InvoiceTotal>
          </InvoiceInfo>
          <ChevronAndBadgeContainer>
            <InvoiceBadge status={invoice.status}>
              {invoiceStatusMap[invoice.status]}
            </InvoiceBadge>
            {expanded ? <ChevronDownMedIcon /> : <ChevronRightMedIcon />}
          </ChevronAndBadgeContainer>
        </HeaderContent>
      </Header>

      {expanded && (
        <Content>
          <Separator />
          {isLoadingTariffs && currentInvoiceId === invoice.id ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "1rem"
              }}
            >
              <Spinner size={SpinnerSize.small} label="Carregando tarifas..." />
            </div>
          ) : (
            <TariffList>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  marginBottom: "8px",
                  justifyContent: "space-between"
                }}
              >
                <InvoiceResume
                  selected={selectedTariff === null}
                  onClick={() => setSelectedTariff(null)}
                />
                <TariffContainer onClick={handleOpenModal} selected={false}>
                  <TariffContent>
                    <DownloadIcon />
                    <TariffName>Exportar Tarifas</TariffName>
                  </TariffContent>
                </TariffContainer>
              </div>

              {preparedTariffs.map(tariff => (
                <TariffItem
                  key={tariff.id}
                  tariff={tariff}
                  selected={selectedTariff?.id === tariff.id}
                  onClick={setSelectedTariff}
                />
              ))}

              {showPagination && (
                <PaginationContainer>
                  <DefaultButton
                    iconProps={{ iconName: "ChevronLeft" }}
                    text="Anterior"
                    disabled={currentPage <= 1 || isLoadingTariffs}
                    onClick={() => onLoadPreviousTariffs(invoice.id)}
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      margin: "10px"
                    }}
                  >
                    Pág: {currentPage}
                    {totalPages > 0 ? ` de ${totalPages}` : ""}
                  </div>
                  <DefaultButton
                    iconProps={{ iconName: "ChevronRight" }}
                    text="Próxima"
                    disabled={!hasMorePages || isLoadingTariffs}
                    onClick={() => onLoadMoreTariffs(invoice.id)}
                  />
                </PaginationContainer>
              )}
            </TariffList>
          )}
        </Content>
      )}

      <ModalListItens
        title="Selecione as Tarifas"
        items={modalTariffItems}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onAction={handleModalAction}
        actionButtonText="Exportar Tarifas"
        isLoading={isExportingTariffs}
        loadingText="Exportando tarifas..."
      />
    </Card>
  );
}
